import { Component, OnInit, HostListener, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { Constant } from '../constant';
import { CommonUtil } from '../util/common-util';
import { ToastUtil } from '../util/toast-util';
import {
  AttachmentItem,
  EventPlanForm,
  EventVendor,
  MyEventDetail,
  ApprovalFlowHistory,
  ApprovalStepDetail,
  MasterItem,
  EventReportForm,
  ParticipantAgeRatio,
} from '../model/my-event';
import { ApprovalRequestDialogComponent } from '../dialog/approval-request-dialog/approval-request-dialog.component';
import { GeneralYesNoDialogComponent } from '../dialog/general-yes-no-dialog/general-yes-no-dialog.component';
import { MatDividerModule } from '@angular/material/divider';
import { Auth } from '@aws-amplify/auth';
import { MyEventEditFormComponent } from '../my-event-edit-form/my-event-edit-form.component';
import { MyReportEditFormComponent } from '../my-report-edit-form/my-report-edit-form.component';
import { StackOnWrapDirective } from '../util/stack-on-wrap.directive';

type FileUploadCategory = 'eval' | 'image' | 'deliverable' | 'reportDoc';

@Component({
  selector: 'app-my-event-detail',
  templateUrl: './my-event-detail.component.html',
  styleUrl: './my-event-detail.component.css',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MyEventEditFormComponent,
    MyReportEditFormComponent,
    StackOnWrapDirective,
  ]
})
export class MyEventDetailComponent implements OnInit, OnDestroy {
  public apiPath = '/myevent/detail';
  public readonly Constant = Constant;
  public showSpinner = true;

  // 再読み込み中の高さ保持用
  // スピナー表示でページ全体が縮むとスクロール位置が切り詰められ、
  // sticky な左メニューの位置がずれるため、読み込み中も高さを維持する
  @ViewChild('mainArea') private mainAreaRef?: ElementRef<HTMLElement>;
  @ViewChild('previewContent') private previewContentRef?: ElementRef<HTMLElement>;
  private lockedMainAreaHeight: number | null = null;
  private lockedPreviewHeight: number | null = null;

  // イベントID
  public eventId: string = '';

  // タブ
  public activeDocumentTab: 'plan' | 'report' = 'plan';
  public activePreviewTab: 'self' | 'other' = 'self';

  // イベント詳細データ
  public eventDetail: MyEventDetail | null = null;

  // 管理用メモ
  public memoContent: string = '';
  public memoSaving: boolean = false;

  // 承認依頼中フラグ
  public isSubmittingApproval: boolean = false;

  // 編集モード
  public isEditMode: boolean = false;

  /** 企画書プレビュー（編集フォーム loadData と同系の取得） */
  public planForm: EventPlanForm = this.createEmptyPlanForm();
  public reportForm: EventReportForm = this.createEmptyReportForm();
  public planMasterData: { [key: string]: MasterItem[] } = {};
  public planAttachments: { 1: AttachmentItem[]; 2: AttachmentItem[] } = { 1: [], 2: [] };
  public planPreviewReady = false;
  public categoryMajorName = '';
  public officialPositionItems: MasterItem[] = [];
  // 対象/参加者（組合役員）で現在選択されているIDの名称（enabled条件なしで解決したもの）。
  // 役職マスタが無効化されていても、選択済みのIDだけは表示上の名前を維持するために使う
  private planParticipantDetailPositions: MasterItem[] = [];
  private _rawCategorySub: (number | null)[] = [null, null, null];
  private _rawImprovementOutcome: number | null = null;
  private _rawImprovementState: (number | null)[] = [null, null, null];
  private _planImplementationMethod: number | null = null;
  targetSelectedNodes: any[] = [];
  private orgMasterData: any = {};

  private routeSub: Subscription | null = null;
  public from: 'draft' | 'approval' | null = null;

  attachments: Record<FileUploadCategory, AttachmentItem[]> = {
    eval: [],
    image: [],
    deliverable: [],
    reportDoc: [],
  };

  constructor(
    private title: Title,
    private router: Router,
    private route: ActivatedRoute,
    private commonUtil: CommonUtil,
    private toastUtil: ToastUtil,
    private dialog: MatDialog,
    private auth: AuthService,
  ) {
    this.title.setTitle(Constant.pageTitleMyEvent + Constant.pageTitleCommon);
  }

  @HostListener('window:focus')
  onFocus(): void {
    this.commonUtil.checkRefreshToken(this.auth);
  }

  ngOnInit() {
    window.scroll(0, 0);

    this.routeSub = this.route.params.subscribe(params => {
      this.eventId = params['id'];
      this.loadEventDetail();
    });

    this.route.queryParams.subscribe(params => {
      this.from = params['from'] || null;

      if (params['edit'] === 'true') {
        this.isEditMode = true;

        // URLからeditを削除
        this.router.navigate(
          [],
          {
            relativeTo: this.route,
            queryParams: {
              edit: null
            },
            queryParamsHandling: 'merge',
            replaceUrl: true
          }
        );
      }
    });
  }

  ngOnDestroy() {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
    this.restoreViewport();
  }

  // 閉じるボタン
  onClose() {
    if (this.from === 'approval') {
      this.router.navigate(['/myevent/approval']);
    } else {
      this.router.navigate(['/myevent']);
    }
  }

  // 削除ボタン
  onDelete() {
    if (!this.commonUtil.checkOnline()) {
      return;
    }

    const dialogRef = this.dialog.open(GeneralYesNoDialogComponent, {
      width: Constant.dialogWidth,
      autoFocus: false,
      data: {
        msg: 'このイベントを削除します。\nよろしいですか？',
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        Auth.currentSession().then(session => {
          const options = this.auth.createApiHeader(session);
          options['body'] = {
            eventId: this.eventId,
          };
          this.commonUtil
            .apiDel(this.apiPath + '/' + this.eventId, options)
            .then(res => {
              if (res.status === Constant.OK) {
                this.toastUtil.showInformationToast('', '削除しました。', Constant.toastShowMiliSec);
                this.router.navigate(['/myevent']);
              }
            })
            .catch(err => {
              this.commonUtil.debug().log(err);
              if (err.message === Constant.msgLoginFailed) {
                this.commonUtil.showReloadDialog(this.auth);
              } else {
                this.toastUtil.showErrorToast('', Constant.msgSaveFailedRetry, Constant.toastShowMiliSec);
              }
            });
        });
      }
    });
  }

  // 帳票タブ切り替え
  onDocumentTabChange(tab: 'plan' | 'report') {
    if (tab === this.activeDocumentTab) return;
    // 表示内容が全面的に入れ替わり、先頭へスクロールするため高さは保持しない
    this.clearContentHeightLock();
    this.showSpinner = true;
    this.activeDocumentTab = tab;
    this.getEventDetail()
    window.scroll(0, 0);
  }

  getFormattedDate(date: string): string {
    if (!date) return '';
    return date.replace(/-/g, '/');
  }

  getStatusInfoFormattedDate(date: string): string {
    if (!date) return '';

    const [datePart, timePart] = date.split(' ');

    return `${datePart.replace(/-/g, '/')} ${timePart?.slice(0, 5) ?? ''}`;
  }

  getApprover(step: any, userId: string) {
    return step.approvers?.find(
      (a: any) => a.id === userId
    );
  }

  get currentStep() {
    const flow = this.eventDetail?.approvalFlows?.find(f => f.isActive);

    return flow?.steps?.find(step =>
      step.statuses?.some(s => s.status === 'reviewing')
    );
  }

  getReversedStatuses(step: any): any[]{
    return [...step.statuses].reverse();
  }

  getStatus(step: any, userId: string) {
    return step.statuses?.find(
      (s: any) => s.userId === userId
    );
  }

  getReversedApprovers(step: any) {
    return [...(step.approvers ?? [])].reverse();
  }

  // プレビュータブ切り替え
  onPreviewTabChange(tab: 'self' | 'other') {
    if (tab === this.activePreviewTab) return;
    this.activePreviewTab = tab;
  }

  // 編集ボタン
  onEdit() {
    if (!this.commonUtil.checkOnline()) {
      return;
    }
    this.isEditMode = true;
    window.scroll(0, 0);
    this.resetMobileZoom();
  }

  // SPでは、詳細（プレビュー）ページが画面より横に広いため、スマホのブラウザが縮小して表示している。
  // 編集は同じページの中で切り替わるので、縮小されたままだと編集画面の見た目が変わってしまう。
  // viewport の指定をいったん書き換えて（等倍・拡大なし）から元に戻し、縮小を解除する。
  // 元に戻す前にもう一度呼ばれても、書き換える前の指定（originalViewport）に戻す（書き換え後の指定を元の指定と取り違えて、拡大できないままにならないように）
  private originalViewport: string | null = null;
  private restoreViewportTimer: ReturnType<typeof setTimeout> | null = null;

  private resetMobileZoom(): void {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) {
      return;
    }
    if (this.restoreViewportTimer === null) {
      this.originalViewport = meta.getAttribute('content') ?? '';
    } else {
      clearTimeout(this.restoreViewportTimer);
    }
    meta.setAttribute('content', 'width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0');
    this.restoreViewportTimer = setTimeout(() => this.restoreViewport(), 300);
  }

  // 書き換えた viewport の指定を元に戻す（書き換えていなければ何もしない）
  private restoreViewport(): void {
    if (this.restoreViewportTimer === null) {
      return;
    }
    clearTimeout(this.restoreViewportTimer);
    this.restoreViewportTimer = null;
    document.querySelector('meta[name="viewport"]')?.setAttribute('content', this.originalViewport ?? '');
  }

  // 編集キャンセル
  onCancelEdit() {
    this.isEditMode = false;
    this.clearContentHeightLock();
    window.scroll(0, 0);
    if (this.activeDocumentTab === 'plan') {
      this.getEventDetail();
      this.loadPlanData();
    }
    if (this.activeDocumentTab === 'report') {
      this.getEventDetail();
      this.loadReportData();
    }
  }

  // 管理用メモ更新
  onUpdateMemo() {
    if (!this.commonUtil.checkOnline()) {
      return;
    }

    // 10000文字以上はカット
    if (this.memoContent.length > 10000) {
      this.memoContent = this.memoContent.substring(0, 10000);
    }

    this.memoSaving = true;

    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);
      options['body'] = {
        eventId: this.eventId,
        admin_memo: this.memoContent
      };
      this.commonUtil
        .apiPost('/myevent/memo', options)
        .then(res => {
          if (res.status === Constant.OK) {
            this.toastUtil.showInformationToast('', 'メモを更新しました。', Constant.toastShowMiliSec);
            // ページ全体を読み込み直すと、ぐるぐるの表示で画面の位置が飛ぶため、メモの最終更新だけ取り直す
            this.refreshAdminMemo(this.auth.createApiHeader(session));
          }
          this.memoSaving = false;
        })
        .catch(err => {
          this.memoSaving = false;
          this.commonUtil.debug().log(err);
          if (err.message === Constant.msgLoginFailed) {
            this.commonUtil.showReloadDialog(this.auth);
          } else {
            this.toastUtil.showErrorToast('', Constant.msgSaveFailedRetry, Constant.toastShowMiliSec);
          }
        });
    }).catch(err => {
      // ログインの情報が取れないときも、更新ボタンを押せる状態に戻す
      this.memoSaving = false;
      this.commonUtil.debug().log(err);
      this.commonUtil.showReloadDialog(this.auth);
    });

    // TODO: API実装後に更新処理を追加
    //setTimeout(() => {
    //  this.memoSaving = false;
    //  if (this.eventDetail) {
    //    this.eventDetail.adminMemo.content = this.memoContent;
    //    this.eventDetail.adminMemo.lastUpdatedAt = this.formatCurrentDateTime();
    //  }
    //  this.toastUtil.showInformationToast('', '更新しました。', Constant.toastShowMiliSec);
    //}, 500);
  }

  // 承認依頼ダイアログを開く
  onOpenApprovalRequest() {
    if (!this.commonUtil.checkOnline()) {
      return;
    }

    // 狭い画面では、組合相談の担当者選択と同じく、画面の左右・上下に16pxずつ余白を残して広く使う
    // （Material の既定では幅が画面の80%までになり、SPでは承認者の一覧が狭くなるため）。
    // 高さは dvh（実際に見えている画面の高さ）で決める（vh だと iPhone で下のボタンが画面の外に出るため）
    const dialogRef = this.dialog.open(ApprovalRequestDialogComponent, {
      width: '900px',
      height: '80dvh',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'min(1000px, calc(100dvh - 32px))',
      disableClose: true,
      autoFocus: false,
      data: {
        eventId: this.eventId,
        documentType: this.activeDocumentTab,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result == 'OK') {
        this.showSpinner = true;
        this.toastUtil.showInformationToast('', '承認依頼を送信しました。', Constant.toastShowMiliSec);
        this.loadEventDetail();
      }
    });
  }

  // 承認ボタン
  onApprove(step: ApprovalStepDetail) {
    if (!this.commonUtil.checkOnline()) {
      return;
    }

    const dialogRef = this.dialog.open(GeneralYesNoDialogComponent, {
      width: Constant.dialogWidth,
      autoFocus: false,
      data: {
        msg: 'このイベントを決裁します。ステータスは完了となります。',
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        Auth.currentSession().then(session => {
          const options = this.auth.createApiHeader(session);
          options['body'] = {
            type: 'approve',
            eventId: this.eventId,
            document_type: this.activeDocumentTab,
          };
          this.commonUtil
            .apiPost('/myevent/approve', options)
            .then(res => {
              if (res.status === Constant.OK) {
                this.toastUtil.showInformationToast('', '承認しました。', Constant.toastShowMiliSec);
                this.showSpinner = true;
                this.loadEventDetail();
              }
            })
            .catch(err => {
              this.commonUtil.debug().log(err);
              if (err.message === Constant.msgLoginFailed) {
                this.commonUtil.showReloadDialog(this.auth);
              } else if (err.message === Constant.msgErrorApplove) {
                this.toastUtil.showErrorToast('', Constant.msgFailedApprovalFlow, Constant.toastShowMiliSec);
              } else {
                this.toastUtil.showErrorToast('', Constant.msgSaveFailedRetry, Constant.toastShowMiliSec);
              }
            });
        });
      }
    });
  }

  // 差戻しボタン
  onReturn(step: ApprovalStepDetail) {
    if (!this.commonUtil.checkOnline()) {
      return;
    }

    const dialogRef = this.dialog.open(GeneralYesNoDialogComponent, {
      width: Constant.dialogWidth,
      autoFocus: false,
      data: {
        msg: 'このイベントを起票者へ差戻します。',
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        Auth.currentSession().then(session => {
          const options = this.auth.createApiHeader(session);
          options['body'] = {
            type: 'return',
            eventId: this.eventId,
            document_type: this.activeDocumentTab,
          };
          this.commonUtil
            .apiPost('/myevent/approve', options)
            .then(res => {
              if (res.status === Constant.OK) {
                this.toastUtil.showInformationToast('', '差戻ししました。', Constant.toastShowMiliSec);
                this.showSpinner = true;
                this.loadEventDetail();
              }
            })
            .catch(err => {
              this.commonUtil.debug().log(err);
              if (err.message === Constant.msgLoginFailed) {
                this.commonUtil.showReloadDialog(this.auth);
              } else if (err.message === Constant.msgErrorReturn) {
                this.toastUtil.showErrorToast('', Constant.msgFailedApprovalFlow, Constant.toastShowMiliSec);
              } else {
                this.toastUtil.showErrorToast('', Constant.msgSaveFailedRetry, Constant.toastShowMiliSec);
              }
            });
        });
        // TODO: API実装後に差戻し処理を追加
      }
    });
  }

  hasVendorContent(vendor: any): boolean {
    return !!(
      vendor.companyName ||
      vendor.workSummary ||
      vendor.workDurationMinutes != null ||
      vendor.specificCost != null
    );
  }

  get visibleVendors(): any[] {
    return (this.planForm?.vendors || []).filter(v =>
      this.hasVendorContent(v)
    );
  }

  // ステータスラベル取得
  getStatusLabel(status: 'draft' | 'reviewing' | 'completed' | 'returned' | 'before_draft'): string {
    if (!status || status == 'before_draft') {
      status = 'draft';
    }

    return Constant.myEventStatuses[
      status as 'draft' | 'reviewing' | 'completed' | 'returned'
    ];
  }

  // 承認ステータスラベル取得
  getApprovalStatusLabel(status: string): string {
    return Constant.myEventApprovalStatuses[status as keyof typeof Constant.myEventApprovalStatuses] || status;
  }

  // 承認フローがアクティブかどうか
  isActiveFlow(flow: ApprovalFlowHistory): boolean {
    return flow.isActive;
  }

  lightboxUrl: string | null = null;

  openLightbox(url: string): void {
    this.lightboxUrl = url;
  }

  closeLightbox(): void {
    this.lightboxUrl = null;
  }

  // 現在の日時をフォーマット
  private formatCurrentDateTime(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  }

  // 読み込み中に維持するメインエリアの高さ
  public get mainAreaMinHeight(): number | null {
    return this.showSpinner || !this.planPreviewReady ? this.lockedMainAreaHeight : null;
  }

  // 読み込み中に維持するプレビューエリアの高さ
  public get previewMinHeight(): number | null {
    return this.planPreviewReady ? null : this.lockedPreviewHeight;
  }

  // 再読み込み前に現在の高さを記録する（スピナー表示中の高さ縮みを防ぐ）
  private lockContentHeight(): void {
    this.lockedMainAreaHeight = this.mainAreaRef?.nativeElement.offsetHeight ?? null;
    this.lockedPreviewHeight = this.previewContentRef?.nativeElement.offsetHeight ?? null;
  }

  // 高さの保持を解除する（表示内容が全面的に入れ替わる場合）
  private clearContentHeightLock(): void {
    this.lockedMainAreaHeight = null;
    this.lockedPreviewHeight = null;
  }

  // 管理用メモの最終更新（日時・更新者）だけを取り直す。ぐるぐるは出さず、画面の位置もそのままにする
  private refreshAdminMemo(options: object): void {
    this.commonUtil
      .apiGet(this.apiPath + '/' + this.eventId + '/' + this.activeDocumentTab, options)
      .then(res => {
        const adminMemo = res.data?.[0]?.adminMemo;
        if (adminMemo && this.eventDetail) {
          this.eventDetail.adminMemo = adminMemo;
        }
      })
      .catch(err => {
        this.commonUtil.debug().log(err);
      });
  }

  // イベント詳細の読み込み
  private loadEventDetail() {
    // スピナー表示に切り替える前に、現在の高さを記録しておく
    this.lockContentHeight();
    this.showSpinner = true;

    // TODO: API実装後に実際のデータ取得処理を追加
    setTimeout(() => {
      this.getEventDetail();
      //this.eventDetail = this.getMockEventDetail();
      //this.memoContent = this.eventDetail.adminMemo.content;
      //this.eventDetail.requiredErrors.length = 0;
      //this.showSpinner = false;
    }, 500);
  }

  private getEventDetail() {
    if (!this.commonUtil.isOnline()) {
      this.showSpinner = false;
      return;
    }

    // ぐるぐる表示
    //this.showSpinner = true;
    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);
      this.commonUtil
        .apiGet(this.apiPath + '/' + this.eventId + '/' + this.activeDocumentTab , options)
        .then(res => {
          this.eventDetail = res.data[0];

          this.eventDetail = {
            ...this.eventDetail,
            approvalFlows: this.convertApprovalFlows(this.eventDetail.approvalFlows)
          };
          this.eventDetail.approvalFlows = this.eventDetail.approvalFlows.sort(
            (a, b) => Number(b.flowId) - Number(a.flowId)
          );

          this.eventDetail.approvalFlows =
          this.eventDetail.approvalFlows.map((flow: any) => ({
            ...flow,
            steps: flow.steps.map((step: any) => ({
              ...step,
              approvers: step.approvers.map((approver: any) => ({
                ...approver,
                organizations_text: approver.organizations_text
                  ? approver.organizations_text
                      .split('\n')
                      .map((s: string) => s.trim())
                      .filter((s: string) => s.length > 0)
                  : []
              }))
            }))
          }));

          if(this.eventDetail.status == 'draft' || this.eventDetail.status === 'returned'|| this.eventDetail.status == 'before_draft'){
            this.eventDetail.canRequestApproval = true;
          } else {
            this.eventDetail.canRequestApproval = false;
          }

          //this.eventDetail.requiredErrors.length = 0;
          this.memoContent = this.eventDetail.adminMemo.content;
          this.commonUtil.debug().log(this.eventDetail);
          this.showSpinner = false;

          if (this.activeDocumentTab === 'plan') {
            this.loadPlanData();
          } else {
            this.loadReportData()
          }
        })
        .catch(err => {
          this.commonUtil.debug().log(err);
          if (err.message === Constant.msgLoginFailed) {
            this.commonUtil.showReloadDialog(this.auth);
          } else if(err.message == 'イベントが存在しません。'){
            this.router.navigate(['/error']);
            return;
          } else {
            this.showSpinner = false;
            this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
          }
        });
    });
  }

  private loadReportData(): void {
    if (!this.eventId || !this.commonUtil.isOnline()) {
      this.planPreviewReady = true;
      return;
    }

    this.planPreviewReady = false;

    Auth.currentSession()
      .then(session => {
        const options = this.auth.createApiHeader(session);
        return Promise.all([
          this.loadPlanMasterData(options),
          this.loadPlanDetail(options),
          this.loadReportFormData(options),
          this.loadOfficialPositions(options),
        ]);
      })
      .then(() => {
        this.applyDefaults();
        this.planPreviewReady = true;
      })
      .catch(err => {
        this.commonUtil.debug().log(err);
        if (err.message === Constant.msgLoginFailed) {
          this.commonUtil.showReloadDialog(this.auth);
        }
        this.planPreviewReady = true;
      });
  }

  private applyDefaults(): void {
    // 企画書自動反映フィールドの名称解決（マスタロード後に実行）
    // my-event-detail と同様に categoryId ↔ categoryMajorName を双方向解決
    if (this.reportForm.categoryId !== null) {
      const major = this.getPlanMasterItems('category_major').find(m => m.id === this.reportForm.categoryId);
      if (major) this.reportForm.categoryMajorName = major.name;
    } else if (this.reportForm.categoryMajorName) {
      const major = this.getPlanMasterItems('category_major').find(m => m.name === this.reportForm.categoryMajorName);
      if (major) this.reportForm.categoryId = major.id;
    }
    this.reportForm.categorySub = this._rawCategorySub
      .filter((id): id is number => id !== null)
      .map(id => this.getPlanMasterItems('category_sub').find(m => m.id === id)?.name ?? '');

    if (this._rawImprovementOutcome !== null) {
      const outcome = this.getPlanMasterItems('improvement_indicator_outcome').find(m => m.id === this._rawImprovementOutcome);
      this.reportForm.improvementOutcomeText = outcome?.name ?? '';
    }
    this.reportForm.improvementStateItems = this._rawImprovementState
      .filter((id): id is number => id !== null)
      .map(id => this.getPlanMasterItems('improvement_indicator_state').find(m => m.id === id)?.name ?? '')
      .filter(name => name !== '');

    // 実施状況：未設定の場合は先頭（実施）をデフォルト
    if (this.reportForm.implementationStatus === null) {
      const statusItems = this.getPlanMasterItems('report_implementation_status');
      if (statusItems.length > 0) this.reportForm.implementationStatus = statusItems[0].id;
    }

    // 実施方法：報告書未設定の場合は企画書の値をデフォルトとして使用
    if (this.reportForm.implementationMethod === null && this._planImplementationMethod !== null) {
      this.reportForm.implementationMethod = this._planImplementationMethod;
    }

    // 参加者評価点数：デフォ「3 普通」（ブランクなし）
    if (this.reportForm.participantEvalScore === null) {
      const evalItems = this.getPlanMasterItems('report_eval_score');
      const futsuu = evalItems.find(m => m.attribute === 'default');
      if (futsuu) this.reportForm.participantEvalScore = futsuu.id;
      else if (evalItems.length >= 3) this.reportForm.participantEvalScore = evalItems[2].id;
    }
    // 改善指標に対する結果：デフォ「予定どおり改善が見込める」（先頭）
    if (this.reportForm.improvementResultRating === null) {
      const resultItems = this.getPlanMasterItems('report_improvement_result');
      if (resultItems.length > 0) this.reportForm.improvementResultRating = resultItems[0].id;
    }
    // 参加者理解度：デフォ「5 十分理解できた」（先頭）
    if (this.reportForm.understandingScore === null) {
      const understandItems = this.getPlanMasterItems('report_understanding_score');
      if (understandItems.length > 0) this.reportForm.understandingScore = understandItems[0].id;
    }

    if (this.reportForm.targetOrgDetail.length > 0) {
      this.targetSelectedNodes = this.buildSelectedNodesFromIds(this.reportForm.targetOrgDetail);
    }
  }

  private loadPlanDetail(options: object): Promise<void> {
    // レスポンスは MyEventDetail 型: category は名称文字列、categoryId は含まれない
    return this.commonUtil.apiGet('/myevent/edit/' + this.eventId, options).then(res => {
      const d = Array.isArray(res.data) ? res.data[0] : res.data;
      if (!d) return;

      this.reportForm.title              = d.title              ?? '';
      this.reportForm.categoryId         = d.categoryId         ?? null;
      this.reportForm.estimatedTotalCost = d.estimatedTotalCost ?? null;
      this._rawCategorySub           = [d.categorySub1 ?? null, d.categorySub2 ?? null, d.categorySub3 ?? null];
      this._rawImprovementOutcome    = d.improvementIndicatorOutcome ?? null;
      this._rawImprovementState      = [d.improvementIndicatorState1 ?? null, d.improvementIndicatorState2 ?? null, d.improvementIndicatorState3 ?? null];
      this._planImplementationMethod = d.implementationMethod ?? null;
    });
  }

  private loadReportFormData(options: object): Promise<void> {
    return this.commonUtil.apiGet('/myevent/report/' + this.eventId, options).then(res => {
      const d = Array.isArray(res.data) ? res.data[0] : res.data;
      if (!d) return;

      // 報告書フィールド
      this.reportForm.status                 = d.status                 ?? 'draft';
      this.reportForm.implementationStatus   = d.implementationStatus   ?? null;
      this.reportForm.implementationMethod   = d.implementationMethod   ?? null;
      this.reportForm.hasProblem             = d.hasProblem             ?? true;
      this.reportForm.problemComment         = d.problemComment         ?? '';
      this.reportForm.stakeholderInfo        = d.stakeholderInfo        ?? '';
      this.reportForm.hasChangeFromPlan      = d.hasChangeFromPlan      ?? true;
      this.reportForm.changeFromPlanComment  = d.changeFromPlanComment  ?? '';
      this.reportForm.actualTotalCost        = d.actualTotalCost        ?? null;
      this.reportForm.costDiffReason         = d.costDiffReason         ?? '';
      this.reportForm.participantTotal       = d.participantTotal       ?? null;
      this.reportForm.participantNew         = d.participantNew         ?? null;
      this.reportForm.ageRatio = {
        under19: d.ageRatioUnder19 ?? null,
        age20s:  d.ageRatio20s     ?? null,
        age30s:  d.ageRatio30s     ?? null,
        age40s:  d.ageRatio40s     ?? null,
        age50s:  d.ageRatio50s     ?? null,
        over60:  d.ageRatioOver60  ?? null,
        unknown: d.ageRatioUnknown ?? false,
      };
      this.reportForm.femaleRatio            = d.femaleRatio            ?? null;
      this.reportForm.implementationContent  = d.implementationContent  ?? '';
      this.reportForm.implementationResult   = d.implementationResult   ?? '';
      this.reportForm.challengesNotes        = d.challengesNotes        ?? '';
      this.reportForm.nextImprovement        = d.nextImprovement        ?? '';
      this.reportForm.negotiationStatus      = d.negotiationStatus      ?? null;
      this.reportForm.agreementContent       = d.agreementContent       ?? '';
      this.reportForm.companyClaim           = d.companyClaim           ?? '';
      this.reportForm.unionClaim             = d.unionClaim             ?? '';
      this.reportForm.unresolvedPoints       = d.unresolvedPoints       ?? '';
      this.reportForm.improvementResultRating = d.improvementResultRating ?? null;
      this.reportForm.improvementResultReason = d.improvementResultReason ?? '';
      this.reportForm.reAttendanceRate        = d.reAttendanceRate       ?? null;
      this.reportForm.participantEvalScore    = d.participantEvalScore   ?? null;
      this.reportForm.evalCollectedCount      = d.evalCollectedCount     ?? null;
      this.reportForm.unevaluatedReason       = d.unevaluatedReason      ?? '';
      this.reportForm.evaluationPart1 = {
        title: d.evaluationPart1Title ?? '',
        score: d.evaluationPart1Score ?? null,
      };
      this.reportForm.evaluationPart2 = {
        title: d.evaluationPart2Title ?? '',
        score: d.evaluationPart2Score ?? null,
      };
      this.reportForm.understandingScore     = d.understandingScore     ?? null;
      this.reportForm.participantOpinions    = d.participantOpinions    ?? '';
      this.reportForm.selfEvalScore          = d.selfEvalScore          ?? null;
      this.reportForm.selfEvalComment        = d.selfEvalComment        ?? '';
      this.reportForm.canRedo                = d.canRedo                ?? null;
      this.reportForm.unionReportMethod = [
        d.unionReportMethod1 ?? null,
        d.unionReportMethod2 ?? null,
        d.unionReportMethod3 ?? null,
      ];
      this.reportForm.memo = d.memo ?? '';

      // 添付ファイル
      const atts: any[] = d.attachments ?? [];
      this.attachments.eval       = atts.filter(a => a.fileCategory === 1).map(this.mapAttachment);
      this.attachments.image      = atts.filter(a => a.fileCategory === 2).map(this.mapAttachment);
      this.attachments.deliverable = atts.filter(a => a.fileCategory === 3).map(this.mapAttachment);
      this.attachments.reportDoc  = atts.filter(a => a.fileCategory === 4).map(this.mapAttachment);
      this.reportForm.targetOrgDetail        = d.targetOrgDetail        ?? [];
      this.reportForm.lastUpdatedName = d.lastUpdatedName ?? '';
      this.reportForm.lastUpdatedOfficialPositionName = d.lastUpdatedOfficialPositionName ?? '';
      this.reportForm.lastUpdatedAt = d.lastUpdatedAt ?? '';
    });
  }

  private mapAttachment(a: any): AttachmentItem {
    return { fileName: a.fileName, filePath: a.filePath, fileKey: '', downloadUrl: a.downloadUrl };
  }

  private loadPlanData(): void {
    if (!this.eventId || !this.commonUtil.isOnline()) {
      this.planPreviewReady = true;
      return;
    }

    this.planPreviewReady = false;

    Auth.currentSession()
      .then(session => {
        const options = this.auth.createApiHeader(session);
        return Promise.all([
          this.loadPlanMasterData(options),
          this.loadPlanFormData(options),
          this.loadOfficialPositions(options),
        ]);
      })
      .then(() => {
        if (this.planForm.categoryId !== null) {
          const found = this.getPlanMasterItems('category_major').find(m => m.id === this.planForm.categoryId);
          if (found) {
            this.categoryMajorName = found.name;
          }
        }
        this.planPreviewReady = true;
      })
      .catch(err => {
        this.commonUtil.debug().log(err);
        if (err.message === Constant.msgLoginFailed) {
          this.commonUtil.showReloadDialog(this.auth);
        }
        this.planPreviewReady = true;
      });
  }

  private loadPlanMasterData(options: object): Promise<void> {
    return this.commonUtil.apiGet('/myevent/master', options).then(res => {
      this.planMasterData = res.data ?? {};
    });
  }

  private loadOfficialPositions(options: object): Promise<void> {
    return this.commonUtil.apiGet('/master', options).then(res => {
      const master = res.data?.master ?? {};
      const positions: any[] = master[5] ?? [];
      this.officialPositionItems = positions.map(p => ({
        id: p.id,
        name: p.name,
        parentId: null,
        attribute: null,
        sortOrder: p.sort_order,
      }));
      this.orgMasterData = master;
    });
  }

  private loadPlanFormData(options: object): Promise<void> {
    return this.commonUtil.apiGet('/myevent/edit/' + this.eventId, options).then(res => {
      const d = Array.isArray(res.data) ? res.data[0] : res.data;

      this.planForm.title = d.title ?? '';
      this.planForm.status = d.status ?? 'draft';
      this.planForm.categoryId = d.categoryId ?? null;
      this.planForm.totalMembers = d.totalMembers ?? null;

      this.planForm.categorySub = [d.categorySub1 ?? null, d.categorySub2 ?? null, d.categorySub3 ?? null];
      this.planForm.publicScope = d.publicScope ?? null;
      this.planForm.publicScopeRoles = d.publicScopeRoles ?? [];
      this.planForm.issueAwareness = [d.issueAwareness1 ?? null, d.issueAwareness2 ?? null, d.issueAwareness3 ?? null];
      this.planForm.issueBasis = d.issueBasis ?? null;
      this.planForm.basisTargetDivision = d.basisTargetDivision ?? null;
      this.planForm.basisTargetPeriod = d.basisTargetPeriod ?? null;
      this.planForm.improvementIndicatorState = [d.improvementIndicatorState1 ?? null, d.improvementIndicatorState2 ?? null, d.improvementIndicatorState3 ?? null];
      this.planForm.improvementIndicatorOutcome = d.improvementIndicatorOutcome ?? null;
      this.planForm.purpose = [d.purpose1 ?? null, d.purpose2 ?? null, d.purpose3 ?? null];
      this.planForm.summary = d.summary ?? '';
      this.planForm.socialTheme = [d.socialTheme1 ?? null, d.socialTheme2 ?? null, d.socialTheme3 ?? null];

      this.planForm.organizerUnit = d.organizerUnit ?? null;
      this.planForm.organizerOrgDetail = d.organizerOrgDetail ?? [];
      this.planForm.organizerOrgDetailText = d.organizerOrgDetailText ?? '';
      this.planForm.targetUnit = d.targetUnit ?? null;
      this.planForm.targetOrgDetail = d.targetOrgDetail ?? [];
      this.planForm.targetOrgDetailText = d.targetOrgDetailText ?? '';
      this.planForm.participantCategory = d.participantCategory ?? null;
      this.planForm.participantDetails = d.participantDetails ?? [];
      this.planParticipantDetailPositions = (d.participantDetailPositions ?? []).map((p: any) => ({
        id: p.id,
        name: p.name,
        parentId: null,
        attribute: null,
        sortOrder: p.sortOrder,
      }));
      this.planForm.companyOtherCollaboration   = d.companyOtherCollaboration   ?? null;
      this.planForm.companyCollaboration = d.companyCollaboration ?? null;
      this.planForm.participantAttributes = d.participantAttributes ?? [];

      this.planForm.implementationMethod = d.implementationMethod ?? null;
      this.planForm.holdingMethod = d.holdingMethod ?? null;
      this.planForm.educationFormat = d.educationFormat ?? [];
      this.planForm.surveyMethod = d.surveyMethod ?? null;
      this.planForm.announcementMethod = [
        d.announcementMethod1 ?? null,
        d.announcementMethod2 ?? null,
        d.announcementMethod3 ?? null,
      ];
      this.planForm.recruitmentMethod = [
        d.recruitmentMethod1 ?? null,
        d.recruitmentMethod2 ?? null,
        d.recruitmentMethod3 ?? null,
      ];

      this.planForm.venue = d.venue ?? null;
      this.planForm.venueDetail = d.venueDetail ?? '';
      this.planForm.numberOfDays = d.numberOfDays ?? '1';
      this.planForm.estimatedParticipants = d.estimatedParticipants ?? null;
      this.planForm.estimatedTotalCost = d.estimatedTotalCost ?? null;
      this.planForm.subsidyAmount = d.subsidyAmount ?? null;
      this.planForm.vendors = d.vendors?.length > 0 ? d.vendors : [this.createEmptyVendor()];
      this.planForm.implementationChallenges = d.implementationChallenges ?? '';
      this.planForm.countermeasures = d.countermeasures ?? '';
      this.planForm.workplaceExpectedIssues = d.workplaceExpectedIssues ?? '';
      this.planForm.keyHearingItems = d.keyHearingItems ?? '';
      this.planForm.effectivenessMeasurement = d.effectivenessMeasurement ?? null;
      this.planForm.meetingFormats = d.meetingFormats ?? [];
      this.planForm.counterpartTypes = d.counterpartTypes ?? [];
      this.planForm.memo = d.memo ?? '';
      this.planForm.schedules =
        d.schedules?.length > 0
          ? d.schedules.map((s: any) => ({
              dayIndex: s.dayIndex,
              eventDate: s.eventDate ?? '',
              // 時刻が空（未入力）のときは空のまま（00:00 にすると「未登録」にならず、承認依頼もできてしまうため）
              startTime: s.startTime ? (s.startTime as string).substring(0, 5) : '',
              endTime: s.endTime ? (s.endTime as string).substring(0, 5) : '',
            }))
          : [{ dayIndex: 1, eventDate: '', startTime: '', endTime: '' }];

      this.planAttachments[1] = (d.attachments ?? [])
        .filter((a: any) => a.fileCategory === 1)
        .map((a: any) => ({
          fileName: a.fileName,
          filePath: a.filePath,
          fileKey: '',
          downloadUrl: a.downloadUrl,
        }));
      this.planAttachments[2] = (d.attachments ?? [])
        .filter((a: any) => a.fileCategory === 2)
        .map((a: any) => ({
          fileName: a.fileName,
          filePath: a.filePath,
          fileKey: '',
          downloadUrl: a.downloadUrl,
        }));
        if (this.planForm.targetOrgDetail.length > 0) {
          this.targetSelectedNodes = this.buildSelectedNodesFromIds(this.planForm.targetOrgDetail);
        }

        this.planForm.lastUpdatedName = d.lastUpdatedName ?? '';
        this.planForm.lastUpdatedOfficialPositionName = d.lastUpdatedOfficialPositionName ?? '';
        this.planForm.lastUpdatedAt = d.lastUpdatedAt ?? '';
    });
  }

  private getPlanMasterItems(type: string): MasterItem[] {
    return this.planMasterData[type] || [];
  }

  // ===== 企画書プレビュー表示（テンプレート用） =====

  readonly planNumberOfDaysOptions = [
    { value: '1', label: '1日' },
    { value: '2', label: '2日' },
    { value: '3', label: '3日' },
    { value: '4', label: '4日' },
    { value: '5', label: '5日' },
    { value: 'period', label: '期間' },
  ];

  private get planCategoryId(): number | null {
    return this.planForm.categoryId;
  }

  get planShowSocialTheme(): boolean {
    return this.planForm.categoryId !== 9;
  }

  get planShowIssueSection(): boolean {
    return this.planCategoryId !== 9;
  }

  get planShowImprovementOutcome(): boolean {
    return this.planCategoryId !== 8 && this.planCategoryId !== 9;
  }

  get planShowTargetOrg(): boolean {
    return this.planCategoryId !== 8;
  }

  get planShowTargetUnit(): boolean {
    return this.planCategoryId !== 8;
  }

  get planShowAttr(): boolean {
    return this.planCategoryId !== 8 && this.planCategoryId !== 9;
  }

  get planShowCompanyCollaboration(): boolean {
    return this.planCategoryId !== 7 && this.planCategoryId !== 8;
  }

  get planshowCompanyOtherCollaboration(): boolean {
    return this.planCategoryId !== 8;
  }

  get planShowHoldingMethod(): boolean {
    return this.planCategoryId === 1 || this.planCategoryId === 10;
  }

  get planShowEducationFormat(): boolean {
    return this.planCategoryId === 2 || this.planCategoryId === 10;
  }

  get planShowSurveyMethod(): boolean {
    return this.planCategoryId === 5 || this.planCategoryId === 7 || this.planCategoryId === 10;
  }

  get planShowAnnouncementMethod(): boolean {
    return this.planCategoryId !== 8 && this.planCategoryId !== 9;
  }

  get planShowRecruitmentMethod(): boolean {
    const c = this.planCategoryId;
    return c !== null && (c <= 3 || c === 6 || c === 10);
  }

  get planShowVenue(): boolean {
    const c = this.planCategoryId;
    return c !== null && (c <= 3 || c >= 9);
  }

  get planShowEstimatedParticipants(): boolean {
    const c = this.planCategoryId;
    return c !== null && (c <= 3 || c === 5 || c === 7 || c >= 9);
  }

  get planShowCostSection(): boolean {
    const c = this.planCategoryId;
    return c !== null && (c <= 3 || c >= 9);
  }

  get planShowSubsidyAndVendors(): boolean {
    const c = this.planCategoryId;
    return c !== null && (c <= 3 || c === 10);
  }

  get planShowWorkplaceSection(): boolean {
    return this.planCategoryId === 5 || this.planCategoryId === 7;
  }

  get planShowEffectiveness(): boolean {
    const c = this.planCategoryId;
    return c !== null && (c <= 3 || c === 10);
  }

  get planShowMeetingSection(): boolean {
    return this.planCategoryId === 8;
  }

  // ===== プレビュー（企画書・報告書、自組織・他組織共通）の行・カードの表示判定 =====
  // 他組織プレビューでは、自組織だけに見せる項目（詳細の箇条書き・外部業者の担当者と連絡先・添付資料・メモなど）を隠す。
  // 「その他」（categoryId=10）は、未入力の項目を「未登録」と出さずに行ごと隠す

  get isOtherPreview(): boolean {
    return this.activePreviewTab === 'other';
  }

  // SPでは外部業者を1社だけ表示し、2社目以降は「残り○社を表示」で開く
  showAllVendors = false;

  get planShowOtherCollaborationRow(): boolean {
    return this.planshowCompanyOtherCollaboration && (this.planCategoryId !== 10 || !!this.getPlanCompanyOtherCollaborationLabel());
  }

  get planShowCompanyCollaborationRow(): boolean {
    return this.planShowCompanyCollaboration && (this.planCategoryId !== 10 || !!this.getPlanCompanyCollaborationLabel());
  }

  get planShowHoldingMethodRow(): boolean {
    return this.planShowHoldingMethod && (this.planCategoryId !== 10 || !!this.planForm.holdingMethod);
  }

  get planShowEducationFormatRow(): boolean {
    return this.planShowEducationFormat && (this.planCategoryId !== 10 || !!this.planForm.educationFormat?.length);
  }

  get planShowSurveyMethodRow(): boolean {
    return this.planShowSurveyMethod && (this.planCategoryId !== 10 || !!this.planForm.surveyMethod);
  }

  get planShowCostRow(): boolean {
    return this.planShowCostSection && (this.planCategoryId !== 10 || !!this.planForm.estimatedTotalCost);
  }

  get planShowSubsidyRow(): boolean {
    return this.planShowSubsidyAndVendors && (this.planCategoryId !== 10 || !!this.planForm.subsidyAmount);
  }

  get planShowEffectivenessRow(): boolean {
    return this.planShowEffectiveness && (this.planCategoryId !== 10 || !!this.planForm.effectivenessMeasurement);
  }

  // 表示する外部業者（他組織は担当者・連絡先を出さないため、それ以外の項目が入っている業者だけ）
  get previewVendors(): EventVendor[] {
    if (!this.planShowSubsidyAndVendors) return [];
    return this.isOtherPreview
      ? this.visibleVendors
      : (this.planForm.vendors ?? []).filter(v => this.hasPlanVendorContent(v));
  }

  // 中の行がすべて非表示になるカードは、見出しごと出さない
  get planShowTargetCard(): boolean {
    return this.planShowEstimatedParticipants || this.planShowTargetOrg || this.planShowMeetingSection;
  }

  get planShowCostCard(): boolean {
    return this.planShowCostRow || this.planShowSubsidyRow;
  }

  get planShowGuideCard(): boolean {
    return this.planShowAnnouncementMethod || this.planShowRecruitmentMethod;
  }

  get planShowIssueCard(): boolean {
    return !!this.planForm.implementationChallenges || !!this.planForm.countermeasures
      || this.planShowWorkplaceSection || this.planShowEffectivenessRow;
  }

  get planShowMaterialCard(): boolean {
    return !this.isOtherPreview
      && (this.planAttachments[1].length > 0 || this.planAttachments[2].length > 0 || !!this.planForm.memo);
  }

  // 複数選択の項目を、マスタの並び（ID順）で名前の配列にする（枠で囲って並べる表示用）
  getPlanMultiNames(ids: number[] | null | undefined, masterKey: string): string[] {
    return [...(ids ?? [])]
      .sort((a, b) => a - b)
      .map(id => this.getPlanMasterName(masterKey, id))
      .filter(Boolean);
  }

  // 実施状況のラベルの色（実施・延期・中止）
  get reportStatusClass(): string {
    switch (this.reportForm.implementationStatus) {
      case 346: return 'postponed';
      case 347: return 'canceled';
      default: return 'done';
    }
  }

  get reportShowParticipantCard(): boolean {
    return !this.isInitialStateReport && !this.isLimitedDisplay && this.showTotalParticipants;
  }

  get reportShowEvaluationCard(): boolean {
    return !this.isInitialStateReport && !this.isLimitedDisplay
      && (this.showEvaluation || (this.showUnderstanding && !!this.reportForm.understandingScore));
  }

  get reportShowSelfEvalCard(): boolean {
    return !this.isInitialStateReport && !this.isLimitedDisplay && this.showEvaluation
      && !!(this.reportForm.selfEvalScore || this.reportForm.selfEvalComment || this.reportForm.canRedo);
  }

  get reportShowCostCard(): boolean {
    return !this.isInitialStateReport && !this.isLimitedDisplay && this.showCost
      && (this.reportForm.categoryId !== 10 || this.reportForm.actualTotalCost != null || !!this.reportForm.costDiffReason);
  }

  get reportShowResultCard(): boolean {
    if (this.isLimitedDisplay) return false;
    if (this.showImprovementResult || this.showOutcomeResult) return true;
    return !this.isInitialStateReport && (
      (this.showStakeholderAndChallenges && !!this.reportForm.challengesNotes)
      || this.showNextImprovement || this.showLaborNegotiation
    );
  }

  get reportShowOtherCard(): boolean {
    if (this.isOtherPreview || this.isInitialStateReport) return false;
    return (!this.isLimitedDisplay && this.showEvaluation && this.attachments.eval.length > 0)
      || (!this.isLimitedDisplay && this.attachments.deliverable.length > 0)
      || this.attachments.reportDoc.length > 0
      || !!this.reportForm.memo;
  }

  get reportShowImages(): boolean {
    return !this.isOtherPreview && !this.isInitialStateReport && !this.isLimitedDisplay
      && this.showImages && this.attachments.image.length > 0;
  }

  // 年代の割合（入力のある年代だけ。「不明」のときは不明だけ）
  get reportAgeItems(): string[] {
    const r = this.reportForm.ageRatio;
    if (!r) return [];
    if (r.unknown) return ['不明'];
    return [
      { label: '19歳以下', value: r.under19 },
      { label: '20代', value: r.age20s },
      { label: '30代', value: r.age30s },
      { label: '40代', value: r.age40s },
      { label: '50代', value: r.age50s },
      { label: '60代以上', value: r.over60 },
    ]
      .filter(a => !!a.value)
      .map(a => `${a.label} ${a.value}%`);
  }

  // イベント各部の評価（タイトル・点数がともに空の部は出さない）
  get reportEvaluationParts(): { label: string; title: string; score: string }[] {
    if (!this.showParticipantDetail) return [];
    return [
      { label: 'イベント：1部', part: this.reportForm.evaluationPart1 },
      { label: 'イベント：2部', part: this.reportForm.evaluationPart2 },
    ]
      .filter(p => p.part?.title || p.part?.score)
      .map(p => ({
        label: p.label,
        title: p.part.title ?? '',
        score: this.getPlanMasterName('report_event_score', p.part.score),
      }));
  }


  private get c(): number | null { return this.reportForm.categoryId; }

  get showProblem(): boolean { return this.c === 9; }

  get showStakeholderAndChallenges(): boolean {
    return this.c === 5 || this.c === 7;
  }

  get showCost(): boolean {
    return this.c !== null && [1, 2, 3, 9, 10].includes(this.c);
  }

  get showTotalParticipants(): boolean {
    return this.c !== null && [1, 2, 3, 5, 7, 9, 10].includes(this.c);
  }

  get showParticipantDetail(): boolean {
    return this.c !== null && [1, 2, 3, 10].includes(this.c);
  }

  get showOrgActivity(): boolean { return this.c === 6; }

  get showOutcomeResult(): boolean {
    return this.c !== null && [1, 2, 3, 5, 6, 7, 10].includes(this.c);
  }

  get showNextImprovement(): boolean {
    return this.c !== null && [1, 2, 3, 5, 6, 7, 10].includes(this.c);
  }

  get ageRatioTotal(): number {
    const r = this.reportForm.ageRatio;
    return (r.under19 || 0) + (r.age20s || 0) + (r.age30s || 0)
         + (r.age40s || 0) + (r.age50s || 0) + (r.over60 || 0);
  }

  get showLaborNegotiation(): boolean { return this.c === 8; }

  get showAgreementContent(): boolean {
    const items = this.getPlanMasterItems('report_negotiation_status');
    const selected = items.find(m => m.id === this.reportForm.negotiationStatus);
    return selected?.attribute === 'shows_agreement';
  }

  get showImprovementResult(): boolean {
    return this.c !== null && [1, 2, 3, 5, 6, 7, 8, 10].includes(this.c);
  }

  get showEvaluation(): boolean {
    return this.c !== null && [1, 2, 3, 6, 10].includes(this.c);
  }

  get collectionRate(): string {
    if (!this.reportForm.evalCollectedCount || !this.reportForm.participantTotal) return '- %';
    return Math.round(this.reportForm.evalCollectedCount / this.reportForm.participantTotal * 100) + '%';
  }

  get isEvaluationUnrated(): boolean {
    const items = this.getPlanMasterItems('report_eval_score');
    const selected = items.find(m => m.id === this.reportForm.participantEvalScore);
    return selected?.attribute === 'unevaluated';
  }

  get showUnderstanding(): boolean { return this.c === 2; }

  get showImages(): boolean {
    return this.c !== null && [1, 2, 3, 5, 6, 7, 9, 10].includes(this.c);
  }

  get isLimitedDisplay(): boolean {
    const items = this.getPlanMasterItems('report_implementation_status');
    const selected = items.find(m => m.id === this.reportForm.implementationStatus);
    return selected?.attribute === 'limited';
  }

  get planScheduleRows(): { index: number; label: string }[] {
    if (this.planForm.numberOfDays === 'period') {
      return [
        { index: 0, label: '初日' },
        { index: 1, label: '最終日' },
      ];
    }
    const count = parseInt(this.planForm.numberOfDays, 10) || 1;
    return Array.from({ length: count }, (_, i) => ({ index: i, label: `${i + 1}日目` }));
  }

  get coverageRate(): string {
    const participants = this.planForm.estimatedParticipants;

    if (!participants || participants <= 0) return '';
    if(this.eventDetail.coverageRate == '-') return '- %'

    return this.eventDetail.coverageRate + '%';
  }

  get costPerPerson(): string {
    if (!this.planForm.estimatedTotalCost || !this.planForm.estimatedParticipants) return '';
    // 一人当たりの費用は「○円/人」と表示する
    return Math.round(this.planForm.estimatedTotalCost / this.planForm.estimatedParticipants).toLocaleString() + '円/人';
  }

  getPlanMasterName(type: string, id: number | null | undefined): string {
    if (id == null) return '';
    return this.getPlanMasterItems(type).find(m => m.id === id)?.name ?? '';
  }

  getPlanPriorityDisplay(arr: (number | null)[], masterKey: string): string {
    return arr
      .filter((id): id is number => id != null)
      .map((id, i) => `${i + 1}. ${this.getPlanMasterName(masterKey, id)}`)
      .filter(s => s.length > 2)
      .join('　');
  }

  getPlanMasterNameArray(
    arr: (number | null)[],
    masterKey: string
  ): string[] {
    return arr
      .filter((id): id is number => id != null)
      .map(id => this.getPlanMasterName(masterKey, id))
      .filter((name): name is string => !!name);
  }

  /**
   * 目的・改善したい指標・社会テーマの選択肢の中で、一番長い文言。
   * 3項目とも、この文言が入る幅を内容の最小幅にして、項目名の横に置くか下に落とすかを決める
   */
  get conceptLongestOptionName(): string {
    return ['purpose', 'improvement_indicator_state', 'social_theme']
      .flatMap(key => this.getPlanMasterItems(key).map(m => m.name ?? ''))
      .reduce((longest, name) => (name.length > longest.length ? name : longest), '');
  }

  getPlanMultiDisplay(
    ids: number[] | null | undefined,
    masterKey: string
  ): string {
    if (!ids?.length) {
      return '';
    }

    return [...ids]
      .sort((a, b) => a - b)
      .map(id => this.getPlanMasterName(masterKey, id))
      .filter(Boolean)
      .join('、');
  }

  // 対象/参加者（組合役員）1件ぶんの名称を解決する。
  // 現在enabledな役職を優先し、見つからなければ（役職マスタが無効化されていても）
  // 選択済みIDとして解決済みのplanParticipantDetailPositionsから探す
  private getPlanParticipantPositionName(id: number): string {
    return (
      this.officialPositionItems.find(m => m.id === id)?.name ??
      this.planParticipantDetailPositions.find(m => m.id === id)?.name ??
      ''
    );
  }

  // 公開範囲の下に「該当する役職：」として表示する役職名（役職マスタの表示順）。
  // 運用以上なら運用〜管理、準管理以上なら準管理・管理、管理以上なら管理の役職をすべて並べる。
  // publicScopeRolesはバックエンドで該当権限の役職だけに絞り、完了前はライブ・完了後はスナップショット。
  // 全員・未登録の場合はnull（何も表示しない）
  get previewScopeRoleNames(): string[] | null {
    // 他組織プレビューでは役職名を出さない。該当する役職が無い場合は見出しごと表示しない
    if (this.activePreviewTab === 'other' || ![287, 288, 289].includes(this.planForm.publicScope as number)) {
      return null;
    }
    const names = (this.planForm.publicScopeRoles ?? []).map(r => r.name);
    return names.length ? names : null;
  }

  getPlanParticipantDetailDisplay(): string {
    if (!this.planForm.participantDetails?.length) {
      return '';
    }

    const ids = this.planForm.participantDetails;

    // master順のIDリスト（組合役員は、現在enabledな役職 + 無効化されていても
    // 選択済みのIDを、削除前のsort_order順のまま合成する。末尾に固定しない）
    const masterOrder =
      this.planForm.participantCategory === 196
        ? [
            ...this.officialPositionItems,
            ...this.planParticipantDetailPositions.filter(
              stale => !this.officialPositionItems.some(m => m.id === stale.id)
            ),
          ]
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            .map(m => m.id)
        : this.getPlanMasterItems('participant_detail').map(m => m.id);

    // masterData順でソート
    const sortedIds = [...ids].sort(
      (a, b) => masterOrder.indexOf(a) - masterOrder.indexOf(b)
    );

    if (this.planForm.participantCategory === 196) {
      return sortedIds
        .map(id => this.getPlanParticipantPositionName(id))
        .filter(Boolean)
        .join('、');
    }

    return sortedIds
      .map(id => this.getPlanMasterName('participant_detail', id))
      .filter(Boolean)
      .join('、');
  }

  getPlanParticipantDetailArray(): string[] {
    if (!this.planForm.participantDetails?.length) {
      return [];
    }

    if (this.planForm.participantCategory === 196) {
      return this.planForm.participantDetails
        .map(id => this.getPlanParticipantPositionName(id))
        .filter(Boolean);
    }

    return this.getPlanMasterNameArray(
      this.planForm.participantDetails,
      'participant_detail'
    );
  }

  getPlanAttrGenerationDisplay(): string {
    const genIds = new Set(
      this.getPlanMasterItems('attr_generation')
        .map(m => m.id)
    );

    return [...(this.planForm.participantAttributes ?? [])]
      .sort((a, b) => a - b)
      .filter(id => genIds.has(id))
      .map(id => this.getPlanMasterName('attr_generation', id))
      .filter(Boolean)
      .join('、');
  }

  getPlanAttrGenderDisplay(): string {
    const genderIds = new Set(
      this.getPlanMasterItems('attr_gender')
        .map(m => m.id)
    );

    return [...(this.planForm.participantAttributes ?? [])]
      .sort((a, b) => a - b)
      .filter(id => genderIds.has(id))
      .map(id => this.getPlanMasterName('attr_gender', id))
      .filter(Boolean)
      .join('、');
  }

  getPlanCompanyOtherCollaborationLabel(): string {
    if (this.planForm.companyOtherCollaboration === true) return 'あり';
    if (this.planForm.companyOtherCollaboration === false) return 'なし';
    return '';
  }

  getPlanCompanyCollaborationLabel(): string {
    if (this.planForm.companyCollaboration === true) return 'あり';
    if (this.planForm.companyCollaboration === false) return 'なし';
    return '';
  }

  getReportHasProblemabel(): string {
    if (this.reportForm.hasProblem === true) return 'あり';
    if (this.reportForm.hasProblem === false) return 'なし';
    return '';
  }

  getReportHasChangeFromPlan(): string {
    if (this.reportForm.hasChangeFromPlan === true) return 'あり';
    if (this.reportForm.hasChangeFromPlan === false) return 'なし';
    return '';
  }

  get costDiff(): string {
    if (this.reportForm.estimatedTotalCost === null || this.reportForm.actualTotalCost === null) return '';
    const diff = this.reportForm.estimatedTotalCost - this.reportForm.actualTotalCost;
    return diff.toLocaleString() + '円';
  }

  getPlanNumberOfDaysLabel(): string {
    return (
      this.planNumberOfDaysOptions.find(o => o.value === this.planForm.numberOfDays)?.label ??
      this.planForm.numberOfDays
    );
  }

  formatPlanDate(date: string | null | undefined): string {
    if (!date) return '';
    return date.replace(/-/g, '/');
  }

  formatPlanScheduleRow(index: number): string {
    const s = this.planForm.schedules[index];

    if (!s) {
      return '';
    }

    const date = s.eventDate
      ? this.formatPlanDate(s.eventDate)
      : '未登録';

    const start = s.startTime
      ? `開始${s.startTime.substring(0, 5)}`
      : '';

    const end = s.endTime
      ? `終了${s.endTime.substring(0, 5)}`
      : '';

    return [date, start, end]
      .filter(Boolean)
      .join(' ');
  }

  getPlanScheduleDisplay(index: number) {
    const s = this.planForm.schedules[index];

    if (!s) {
      return null;
    }

    const start = s.startTime ?? '';
    const end = s.endTime ?? '';

    let duration = '';

    if (start && end) {
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);

      const startMin = sh * 60 + sm;
      const endMin = eh * 60 + em;

      const diff = endMin - startMin;

      // 実施時間は「所要 02:30」の形（時間・分とも2桁）で表示する
      if (diff > 0) {
        const h = Math.floor(diff / 60);
        const m = diff % 60;
        duration = `所要 ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
    }

    return {
      date: s.eventDate
        ? this.formatPlanDate(s.eventDate)
        : null,
      start,
      end,
      duration,
    };
  }

  formatPlanDuration(minutes: number | null | undefined): string {
    if (minutes == null) return '';
    const h = Math.floor(minutes / 60);
    const min = minutes % 60;
    if (h > 0 && min > 0) return `${h}時間${min}分`;
    if (h > 0) return `${h}時間`;
    return `${min}分`;
  }

  formatPlanCurrency(value: number | null | undefined): string {
    if (value == null) return '';
    return Number(value).toLocaleString();
  }

  hasPlanVendorContent(vendor: EventVendor): boolean {
    return !!(
      vendor.companyName?.trim() ||
      vendor.contactPerson?.trim() ||
      vendor.contactInfo?.trim() ||
      vendor.workSummary?.trim() ||
      vendor.workDurationMinutes != null ||
      vendor.specificCost != null
    );
  }

  getVendorCostLabel(cost: any): string {
    const n = Number(cost);
    if (cost === null || cost === undefined || cost === '') return '';
    if (n >= 5_010_000) return '501万円以上';
    if (n >= 4_010_000) return '401万円〜500万円';
    if (n >= 3_010_000) return '301万円〜400万円';
    if (n >= 2_010_000) return '201万円〜300万円';
    if (n >= 1_010_000) return '101万円〜200万円';
    if (n >= 510_000)   return '51万円〜100万円';
    if (n >= 410_000)   return '41万円〜50万円';
    if (n >= 310_000)   return '31万円〜40万円';
    if (n >= 210_000)   return '21万円〜30万円';
    if (n >= 110_000)   return '11万円〜20万円';
    if (n >= 60_000)    return '6万円〜10万円';
    if (n >= 10_000)    return '1万円〜5万円';
    else return '1万円未満'
  }

  get hasVisibleVendors(): boolean {
    return this.planForm.vendors?.some(v => this.hasPlanVendorContent(v)) ?? false;
  }

  get subsidyRate(): string {
    if (!this.planForm.subsidyAmount || !this.planForm.estimatedTotalCost) return '';
    return Math.round(this.planForm.subsidyAmount / this.planForm.estimatedTotalCost * 100) + '%';
  }

  async onDownloadPlanAttachment(att: AttachmentItem): Promise<void> {
    if (!att.downloadUrl) return;

    try {
      const response = await fetch(att.downloadUrl);
      const arrayBuffer = await response.arrayBuffer();
      // ブラウザでファイルが開かれないよう、汎用バイナリ型にしてダウンロードさせる
      const blob = new Blob([arrayBuffer], { type: Constant.fileFormatDownload });

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = att.fileName || 'download';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);

    } catch (e) {
      console.error('download error', e);
    }
  }

  private createEmptyReportForm(): EventReportForm {
    return {
      status: 'draft',
      title: '',
      categoryId: null,
      categoryMajorName: '',
      categorySub: [],
      estimatedTotalCost: null,
      improvementOutcomeText: '',
      improvementStateItems: [],
      implementationStatus: null,
      implementationMethod: null,
      hasProblem: true,
      problemComment: '',
      stakeholderInfo: '',
      hasChangeFromPlan: true,
      changeFromPlanComment: '',
      actualTotalCost: null,
      costDiffReason: '',
      participantTotal: null,
      participantNew: null,
      ageRatio: this.createEmptyAgeRatio(),
      femaleRatio: null,
      implementationContent: '',
      implementationResult: '',
      challengesNotes: '',
      nextImprovement: '',
      negotiationStatus: null,
      agreementContent: '',
      companyClaim: '',
      unionClaim: '',
      unresolvedPoints: '',
      improvementResultRating: null,
      improvementResultReason: '',
      reAttendanceRate: null,
      participantEvalScore: null,
      evalCollectedCount: null,
      unevaluatedReason: '',
      evaluationPart1: { title: '', score: null },
      evaluationPart2: { title: '', score: null },
      understandingScore: null,
      participantOpinions: '',
      selfEvalScore: null,
      selfEvalComment: '',
      canRedo: null,
      unionReportMethod: [null, null, null],
      memo: '',
      targetOrgDetail: [],
      lastUpdatedName: null,
      lastUpdatedOfficialPositionName: null,
      lastUpdatedAt: null,
    };
  }

  private createEmptyAgeRatio(): ParticipantAgeRatio {
    return { under19: null, age20s: null, age30s: null, age40s: null, age50s: null, over60: null, unknown: false };
  }

  private createEmptyPlanForm(): EventPlanForm {
    return {
      title: '',
      categoryId: null,
      status: 'draft',
      categorySub: [null, null, null],
      publicScope: null,
      issueAwareness: [null, null, null],
      issueBasis: null,
      basisTargetDivision: null,
      basisTargetPeriod: null,
      improvementIndicatorState: [null, null, null],
      improvementIndicatorOutcome: null,
      purpose: [null, null, null],
      summary: '',
      socialTheme: [null, null, null],
      organizerUnit: null,
      organizerOrgDetail: [],
      organizerOrgDetailText: '',
      targetUnit: null,
      targetOrgDetail: [],
      targetOrgDetailText: '',
      participantCategory: null,
      participantDetails: [],
      companyOtherCollaboration: null,
      companyCollaboration: null,
      participantAttributes: [],
      implementationMethod: null,
      holdingMethod: null,
      educationFormat: [],
      surveyMethod: null,
      announcementMethod: [null, null, null],
      recruitmentMethod: [null, null, null],
      venue: null,
      venueDetail: '',
      numberOfDays: '1',
      estimatedParticipants: null,
      estimatedTotalCost: null,
      subsidyAmount: null,
      vendors: [this.createEmptyVendor()],
      implementationChallenges: '',
      countermeasures: '',
      workplaceExpectedIssues: '',
      keyHearingItems: '',
      effectivenessMeasurement: null,
      meetingFormats: [],
      counterpartTypes: [],
      memo: '',
      schedules: [{ dayIndex: 1, eventDate: '', startTime: '', endTime: '' }],
      totalMembers: null,
      lastUpdatedName: null,
      lastUpdatedOfficialPositionName: null,
      lastUpdatedAt: null,
    };
  }

  private createEmptyVendor(): EventVendor {
    return {
      companyName: '',
      contactPerson: '',
      contactInfo: '',
      workSummary: '',
      workDurationMinutes: null,
      specificCost: null,
    };
  }

  private buildSelectedNodesFromIds(ids: number[]): any[] {
    if (!ids || ids.length === 0) return [];

    const idSet = new Set(ids);
    const result: any[] = [];

    // #skipを透過して最初の非skipレベルの人数を合計する
    // - #skipノードは透過して子を再帰探索
    // - 非skipノードに到達したらmember_countを取得し、それより下は見ない
    const sumChushinCount = (nodes: any[]): number =>
      nodes.reduce((sum: number, n: any) => {
        if (n.name?.includes('#skip')) return sum + sumChushinCount(n.children ?? []);
        return sum + (n.member_count || 0);
      }, 0);

    // 全体（先頭固定）a
    if (idSet.has(-1)) {
      const zenbuCount = ['1', '2', '3', '4'].reduce((total, key) => {
        return total + sumChushinCount(this.orgMasterData[key] ?? []);
      }, 0);
      result.push({ id: -1, name: '全体', path: '全体', master_type: 0, org_count: zenbuCount, orgCountForCoverage: zenbuCount });
    }

    type Entry = { id: number; name: string; memberCount: number | null; masterType: number; ancestorIds: number[]; isSkip: boolean; isVirtualRoot: boolean };
    const allEntries: Entry[] = [];

    const virtualRoots = [
      { id: 1, name: '本部',  masterType: 1, key: '1' },
      { id: 2, name: '支部',  masterType: 2, key: '2' },
      { id: 3, name: 'エリア', masterType: 3, key: '3' },
      { id: 4, name: '専門部', masterType: 4, key: '4' },
    ];

    for (const vr of virtualRoots) {
      allEntries.push({ id: vr.id, name: vr.name, memberCount: null, masterType: vr.masterType, ancestorIds: [], isSkip: false, isVirtualRoot: true });

      const traverse = (nodes: any[], ancestorIds: number[]) => {
        for (const n of nodes) {
          const isSkip = n.name?.includes('#skip');
          allEntries.push({ id: n.id, name: n.name, memberCount: n.member_count ?? null, masterType: vr.masterType, ancestorIds: [...ancestorIds], isSkip, isVirtualRoot: false });
          if (n.children) traverse(n.children, [...ancestorIds, n.id]);
        }
      };
      traverse(this.orgMasterData[vr.key] ?? [], [vr.id]);
    }

    const selectedEntries = allEntries.filter(e => idSet.has(e.id));
    const selectedIdSet = new Set(selectedEntries.map(e => e.id));

    for (const e of selectedEntries) {
      // 大分類（仮想ルート）が選択済みの場合、その下の中分類は二重カウントしない
      const hasCheckedParentWithOrgCount = e.ancestorIds.some(aid => {
        if (!selectedIdSet.has(aid)) return false;
        const anc = allEntries.find(a => a.id === aid);
        return anc != null && (anc.memberCount != null || anc.isVirtualRoot);
      });

      let orgCountForCoverage: number;
      if (hasCheckedParentWithOrgCount) {
        orgCountForCoverage = 0;
      } else if (e.isVirtualRoot) {
        // 大分類が選択された場合: チェックの有無にかかわらず全中分類の人数を合計
        orgCountForCoverage = sumChushinCount(this.orgMasterData[String(e.masterType)] ?? []);
      } else {
        orgCountForCoverage = e.memberCount || 0;
      }

      const pathParts: string[] = [];
      for (const aid of e.ancestorIds) {
        const anc = allEntries.find(a => a.id === aid);
        if (anc && !anc.isSkip) pathParts.push(anc.name);
      }
      if (!e.isSkip) pathParts.push(e.name);

      result.push({ id: e.id, name: e.name, org_count: e.memberCount, master_type: e.masterType, path: pathParts.join(' '), orgCountForCoverage });
    }

    // 他組織（末尾固定）
    if (idSet.has(-2)) result.push({ id: -2, name: '他組織', path: '他組織', master_type: 0, org_count: null, orgCountForCoverage: 0 });

    return result;
  }

  private convertApprovalFlows(data: any[]): any[] {
    const typeMap: { [key: number]: string } = {
      1: '本部',
      2: '支部',
      3: 'エリア'
    };

    return data.map(flow => ({
      ...flow,
      steps: flow.steps.map((step: any) => {
        const approvers = (step.approvers ?? []).map((user: any) => {
          const affiliations = (user.organizations ?? []).map((org: any) => {
            const prefix = typeMap[org.master_type] || '';

            return org.level === 0
              ? org.path
              : `${prefix} ${org.path}`.trim();
          });

          const concurrentAffiliations =
            (user.concurrent_organizations ?? []).map((org: any) =>
              `専門部 ${org.path}`
            );

          return {
            id: user.id,
            name: user.name ?? '',
            position: user.position ?? '',
            affiliations: [
              ...affiliations,
              ...concurrentAffiliations
            ],
            organizations_text: user.organizations_text ?? ''
          };
        });

        return {
          ...step,
          approvers
        };
      })
    }));
  }

  public get canEdit(): boolean {
    if (!this.eventDetail) {
      return false;
    }

    // completedは全員不可
    if (this.eventDetail.status === 'completed') {
      return false;
    }

    // draft
    if (
      (this.eventDetail.status === 'draft' || this.eventDetail.status == 'before_draft') &&
      this.eventDetail.isDrafter
    ) {
      return true;
    }

    // returned
    if (
      this.eventDetail.status === 'returned' &&
      this.eventDetail.isDrafter
    ) {
      return true;
    }

    // reviewing
    if (this.eventDetail.status === 'reviewing') {
      // 承認ボール保持者のみ
      return this.eventDetail.approvalFlows?.some((flow: any) =>
        flow.isActive &&
        flow.steps?.some((step: any) =>
          step.canApprove || step.canReturn
        )
      );
    }

    return false;
  }

  public get canDelete(): boolean {
    if (!this.eventDetail) {
      return false;
    }

    // 起票者のみ
    if (!this.eventDetail.isDrafter) {
      return false;
    }

    // reportでは削除不可
    if (this.activeDocumentTab !== 'plan') {
      return false;
    }

    // completed後は削除不可
    if (this.eventDetail.status === 'completed') {
      return false;
    }

    // 承認依頼できる状態のみ
    return this.eventDetail.canRequestApproval;
  }


  public get canViewReportTab(): boolean {
    if (!this.eventDetail) {
      return false;
    }

    // 起票者
    if (this.eventDetail.isDrafter) {
      // 企画完了後
      if (this.eventDetail.status === 'completed') {
        return true;
      }

      // report閲覧中
      if (this.eventDetail.documentType === 'report') {
        return true;
      }
    } else {
      // 報告書が存在しない
      if (!this.eventDetail.hasReport) {
        return false;
      }

      // 報告書承認者
      if (
        this.eventDetail.isReportApprover
      ) {
        return true;
      }

      return false;
    }
  }

  public get canUpdateMemo(): boolean {
    if (this.eventDetail.canEditMemo) {
      return true;
    }

    // 起票者
    if (this.eventDetail.isDrafter) {
      return true;
    }

    return false;

  }

  get isInitialState(): boolean {
    return !this.planForm.lastUpdatedName;
  }

  get isInitialStateReport(): boolean {
    return !this.reportForm.lastUpdatedName;
  }

  // 報告書は下書きに着手するまで（before_draft）最終更新を表示しない
  get showLastUpdated(): boolean {
    if (this.activeDocumentTab === 'plan') {
      return true;
    }

    return !!this.reportForm.lastUpdatedAt && this.reportForm.status !== 'before_draft';
  }

  get hasRequiredFieldsMissing(): boolean {
    if (this.activeDocumentTab === 'report') {
      return this.hasRequiredReportFieldsMissing;
    }

    return this.hasRequiredPlanFieldsMissing;
  }

  get hasRequiredPlanFieldsMissing(): boolean {
    if (this.isBlank(this.planForm.title)) {
      return true;
    }

    if (this.isBlank(this.planForm.categoryId)) {
      return true;
    }

    if (
      !this.getPlanMasterNameArray(
        this.planForm.categorySub,
        'category_sub'
      )?.length
    ) {
      return true;
    }


    if (this.isBlank(this.planForm.publicScope)) {
      return true;
    }

    if (this.planShowIssueSection) {
      // 改善したい指標
      if (
        !this.getPlanMasterNameArray(
          this.planForm.improvementIndicatorState,
          'improvement_indicator_state'
        )?.length
      ) {
        return true;
      }

      if(this.planShowTargetOrg){
        if (this.isBlank(this.planForm.improvementIndicatorOutcome)) {
          return true;
        }
      }

      // 目的
      if (
        !this.getPlanMasterNameArray(
          this.planForm.purpose,
          'purpose'
        )?.length
      ) {
        return true;
      }
    }

    // 概要
    if (this.isBlank(this.planForm.summary)) {
      return true;
    }

    if (this.planShowSocialTheme) {
      if (
        !this.getPlanMasterNameArray(
          this.planForm.socialTheme,
          'social_theme'
        )?.length
      ) {
        return true;
      }
    }

    // =========================
    // 開催者所属組織
    // =========================
    if (this.isBlank(this.planForm.organizerUnit)) {
      return true;
    }

    if (this.isBlank(this.planForm.organizerOrgDetailText)) {
      return true;
    }

    if (this.planShowTargetOrg) {
      if (this.isBlank(this.planForm.targetUnit)) {
        return true;
      }

      if (this.isBlank(this.planForm.targetOrgDetailText)) {
        return true;
      }

      // 対象/参加者
      if (this.isBlank(this.planForm.participantCategory)) {
        return true;
      }

      if (
        this.planForm.participantCategory !== 199 &&
        !this.getPlanParticipantDetailDisplay()
      ) {
        return true;
      }

    }


    if (this.isBlank(this.planForm.implementationMethod)) {
      return true;
    }

    if (
      this.planShowEducationFormat &&
      this.planCategoryId !== 10 &&
      !this.getPlanMultiDisplay(
        this.planForm.educationFormat,
        'education_format'
      )
    ) {
      return true;
    }

    if (this.planShowAnnouncementMethod) {
      if (
        !this.getPlanMasterNameArray(
          this.planForm.announcementMethod,
          'announcement_method'
        )?.length
      ) {
        return true;
      }
    }

    if (this.planShowRecruitmentMethod) {
      if (
        !this.getPlanMasterNameArray(
          this.planForm.recruitmentMethod,
          'recruitment_method'
        )?.length
      ) {
        return true;
      }
    }

    if (this.planShowVenue) {
      if (this.isBlank(this.planForm.venue)) {
        return true;
      }
    }


    if (!this.planScheduleRows?.length) {
      return true;
    }

    for (const row of this.planScheduleRows) {
      const schedule = this.getPlanScheduleDisplay(row.index);

      if (
        !schedule?.date ||
        !schedule?.start ||
        !schedule?.end ||
        schedule.start > schedule.end
      ) {
        return true;
      }
    }

    if (this.planShowEstimatedParticipants) {
      if (this.isBlank(this.planForm.estimatedParticipants)) {
        return true;
      }
    }
    if (
      this.planShowCostSection &&
      this.planForm.categoryId != 10
    ) {
      if (this.isBlank(this.planForm.estimatedTotalCost)) {
        return true;
      }
    }

    if (
      this.planShowSubsidyAndVendors &&
      this.planForm.categoryId != 10
    ) {
      if (this.isBlank(this.planForm.subsidyAmount)) {
        return true;
      }
    }

    if (this.planShowWorkplaceSection) {
      if (this.isBlank(this.planForm.workplaceExpectedIssues)) {
        return true;
      }

      if (this.isBlank(this.planForm.keyHearingItems)) {
        return true;
      }
    }

    if (this.planShowMeetingSection) {
      if (
        !this.planForm.meetingFormats ||
        this.planForm.meetingFormats.length === 0
      ) {
        return true;
      }


      if (!this.getPlanMultiDisplay(this.planForm.counterpartTypes, 'counterpart_type')) {
        return true;
      }
    }

    return false;
  }

  get hasRequiredReportFieldsMissing(): boolean {
    if (this.isBlank(this.planForm.title)) {
      return true;
    }

    if (!this.categoryMajorName) {
      return true;
    }

    if (!this.planForm.categorySub?.length) {
      return true;
    }


      if (this.isBlank(this.reportForm.implementationStatus)) {
        return true;
      }

      if (!this.isLimitedDisplay &&
          this.isBlank(this.reportForm.implementationMethod)) {
        return true;
      }

      if (this.isBlank(this.getReportHasProblemabel())) {
        return true;
      }

      if (!this.isLimitedDisplay &&
          this.showStakeholderAndChallenges &&
          this.isBlank(this.reportForm.stakeholderInfo)) {
        return true;
      }

      if (this.isBlank(this.getReportHasChangeFromPlan())) {
        return true;
      }

      if (!this.isLimitedDisplay &&
          this.showCost &&
          this.reportForm.categoryId !== 10 &&
          this.reportForm.actualTotalCost == null) {
        return true;
      }

      if (!this.isLimitedDisplay &&
          this.showOrgActivity &&
          this.isBlank(this.reportForm.implementationContent)) {
        return true;
      }

      if (!this.isLimitedDisplay &&
          this.showOutcomeResult &&
          this.isBlank(this.reportForm.implementationResult)) {
        return true;
      }

      if (!this.isLimitedDisplay &&
          this.showNextImprovement &&
          this.isBlank(this.reportForm.nextImprovement)) {
        return true;
      }

      if (!this.isLimitedDisplay &&
          this.showLaborNegotiation &&
          this.isBlank(this.reportForm.negotiationStatus)) {
        return true;
      }

      if (!this.isLimitedDisplay &&
          this.showLaborNegotiation &&
          this.showAgreementContent &&
          this.isBlank(this.reportForm.agreementContent)) {
        return true;
      }

      if (!this.isLimitedDisplay &&
          this.showLaborNegotiation &&
          this.isBlank(this.reportForm.companyClaim)) {
        return true;
      }

      if (!this.isLimitedDisplay &&
          this.showLaborNegotiation &&
          this.isBlank(this.reportForm.unionClaim)) {
        return true;
      }

      if (!this.isLimitedDisplay &&
          this.showImprovementResult &&
          this.isBlank(this.reportForm.improvementResultRating)) {
        return true;
      }

    if (!this.isLimitedDisplay &&
        this.showTotalParticipants &&
        this.reportForm.participantTotal == null) {
      return true;
    }

    if (!this.isLimitedDisplay &&
        this.showTotalParticipants &&
        this.showParticipantDetail &&
        this.reportForm.participantNew == null) {
      return true;
    }

    if (!this.isLimitedDisplay &&
        this.showParticipantDetail &&
        this.reportForm.femaleRatio == null) {
      return true;
    }

    if (!this.isLimitedDisplay &&
        this.showEvaluation &&
        this.isBlank(this.reportForm.participantEvalScore)) {
      return true;
    }

    if (!this.isLimitedDisplay &&
        this.showEvaluation &&
        !this.isEvaluationUnrated &&
        this.reportForm.evalCollectedCount == null) {
      return true;
    }

    if (!this.isLimitedDisplay &&
        this.showEvaluation &&
        this.isEvaluationUnrated &&
        this.isBlank(this.reportForm.unevaluatedReason)) {
      return true;
    }

    if (!this.isLimitedDisplay &&
        this.showImprovementResult &&
        !this.getPlanMasterNameArray(
          this._rawImprovementState,
          'improvement_indicator_state'
        )?.length) {
      return true;
    }

    if (!this.isLimitedDisplay &&
        !this.getPlanMasterNameArray(
          this.reportForm.unionReportMethod,
          'report_union_method'
        )?.length) {
      return true;
    }

    return false;
  }


  private isBlank(value: any): boolean {
    if (value == null) {
      return true;
    }

    if (typeof value === 'string') {
      return value.trim() === '';
    }

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    return false;
  }

}
